import { playersAbove } from "../document/band.ts";
import { applyCommand, type Command } from "../document/commands.ts";
import { EMPTY_TILE } from "../document/packed-tile.ts";
import type { MapDocument, PropInstance, TerrainLayer, TileRef, TilesetRef, WorldDocument } from "../document/types.ts";
import { removeLayerCommand } from "./panel-commands.ts";
import { propFootprint, type FrameSizeOf } from "./prop-hit.ts";
import { cornerFills, paintedColour, scanReplace, unresolvedTerrainCells, type ReplaceScope, type ReplaceTerrainEntry } from "./replace.ts";

/**
 * Merging replaced terrain into a layer that already paints the entry (ADR-097's amendment of 2026-10-12,
 * decisions 26 to 29), the pure part. A whole-layer replace keeps its layer and retargets it, which leaves two
 * layers of one entry when another already paints it; a merge instead paints the source's set corners on that
 * other layer, in the entry's colour, and removes the source. The target keeps its id, its name, its group and
 * its place, so the paint moves to the target's place in the draw order: a merge is refused when a layer
 * between the two draws in a cell the source draws, since the paint would cross it. What a terrain layer draws
 * is read from its resolved tiles, where the editor holds them: a terrain's rules and its face draw in cells
 * none of whose corners the layer sets.
 *
 * The command is a `compound` of commands that already exist, one `fillCells` on the target and one
 * `removeLayer` a source, so it inverts exactly and becomes operations as any command does.
 */

/**
 * Why a merge may not run: a layer between the source and the target draws in a cell the source draws, or the
 * target is locked or hidden. `layerId` is the layer between, or the target.
 */
export type MergeRefusal = { readonly why: "between"; readonly layerId: string } | { readonly why: "locked" | "hidden"; readonly layerId: string };

/**
 * What a merge needs from the editor: the colour an unpainted terrain layer is set to paint, why the tools may
 * not edit a layer, a prop's art (a frame's size, the tile size and the frame the map shows a prop with), and a
 * terrain layer's resolved tiles, one packed tile a cell with its rules and face applied, or null when the
 * editor holds none for the layer.
 */
export interface MergeEnv {
  readonly colourOf: (layerId: string) => number;
  readonly blocked: (layerId: string) => "locked" | "hidden" | null;
  readonly sizeOf: FrameSizeOf;
  readonly tileSize: number;
  readonly frameOf: (layerId: string, prop: PropInstance) => TileRef;
  readonly resolved: (layerId: string) => Readonly<Uint32Array> | null;
}

/**
 * A merge ready to run: the command; the corners of the sources it moves; the layer it writes; the layers it
 * removes, in the order given; and the cells of the merged target whose corners match no tile of the entry.
 */
export interface ReplaceMergePlan {
  readonly command: Extract<Command, { readonly kind: "compound" }>;
  readonly corners: number;
  readonly layers: readonly string[];
  readonly removed: readonly string[];
  readonly unresolved: number;
}

/**
 * Whether a terrain layer has a set corner.
 *
 * @param layer The layer.
 * @returns True when it is painted.
 */
function painted(layer: TerrainLayer): boolean {
  return layer.corners.some((colour) => colour !== 0);
}

/**
 * The layer a merge goes into: among the terrain layers that paint the entry (by the colour their corners hold
 * or, unpainted, the colour the editor has them set to paint) and are not themselves being replaced, the
 * topmost with a set corner, else the topmost. The layers the tools may edit come first: a locked or hidden one
 * is named only when no other paints the entry, so the dialog can say why the merge waits.
 *
 * @param map The map.
 * @param sources The layers being replaced.
 * @param to The entry: its terrain's id and its colour.
 * @param env The unpainted layers' colours and why a layer refuses edits.
 * @returns The target's id, or null when no other layer paints the entry.
 */
export function mergeTarget(map: MapDocument, sources: ReadonlySet<string>, to: { readonly terrain: string; readonly colour: number }, env: Pick<MergeEnv, "colourOf" | "blocked">): string | null {
  const candidates = [...map.layers].reverse().filter((layer): layer is TerrainLayer => layer.kind === "terrain" && layer.terrain === to.terrain && !sources.has(layer.id) && (paintedColour(layer) || env.colourOf(layer.id)) === to.colour);
  const editable = candidates.filter((layer) => env.blocked(layer.id) === null);
  const pool = editable.length > 0 ? editable : candidates;
  return (pool.find(painted) ?? pool[0])?.id ?? null;
}

/**
 * The cells a terrain layer draws in: every cell its resolved tiles hold a tile in, so what its rules and its
 * face draw beyond its corners counts and a cell that resolves to no tile does not; without resolved tiles,
 * every cell with a set corner.
 *
 * @param map The map.
 * @param layer The layer.
 * @param env The layer's resolved tiles.
 * @returns One byte a cell, non-zero where it draws.
 */
function drawnCells(map: MapDocument, layer: TerrainLayer, env: Pick<MergeEnv, "resolved">): Uint8Array {
  const stride = map.width + 1;
  const drawn = new Uint8Array(map.width * map.height);
  const tiles = env.resolved(layer.id);
  if (tiles !== null && tiles.length === drawn.length) {
    return drawn.map((_, index) => (tiles[index] === EMPTY_TILE ? 0 : 1));
  }
  for (let y = 0; y < map.height; y += 1) {
    for (let x = 0; x < map.width; x += 1) {
      const at = y * stride + x;
      if (layer.corners[at] !== 0 || layer.corners[at + 1] !== 0 || layer.corners[at + stride] !== 0 || layer.corners[at + stride + 1] !== 0) {
        drawn[y * map.width + x] = 1;
      }
    }
  }
  return drawn;
}

/**
 * Whether a layer draws in any of the given cells: a tile layer with a tile there, a terrain layer that draws
 * there (`drawnCells`), or a prop layer with a prop whose cells (`propFootprint`, for the frame the map shows
 * it with) hold the cell. Layers of every other kind draw nothing.
 *
 * @param map The map.
 * @param layer The layer.
 * @param cells One byte a cell, non-zero for the cells asked about.
 * @param env A prop's art and a terrain layer's resolved tiles.
 * @returns True when it does.
 */
function drawsIn(map: MapDocument, layer: MapDocument["layers"][number], cells: Readonly<Uint8Array>, env: Pick<MergeEnv, "sizeOf" | "tileSize" | "frameOf" | "resolved">): boolean {
  if (layer.kind === "tile") {
    return layer.tiles.some((packed, index) => packed !== EMPTY_TILE && cells[index] !== 0);
  }
  if (layer.kind === "terrain") {
    return drawnCells(map, layer, env).some((drawn, index) => drawn !== 0 && cells[index] !== 0);
  }
  if (layer.kind !== "prop") {
    return false;
  }
  return layer.props.some((prop) => {
    const held = propFootprint({ ...prop, frame: env.frameOf(layer.id, prop) }, env.sizeOf, env.tileSize);
    const x0 = Math.max(0, held.x0);
    const x1 = Math.min(map.width, held.x1) - 1;
    const y0 = Math.max(0, held.y0);
    const y1 = Math.min(map.height, held.y1) - 1;
    for (let y = y0; y <= y1; y += 1) {
      for (let x = x0; x <= x1; x += 1) {
        if (cells[y * map.width + x] !== 0) {
          return true;
        }
      }
    }
    return false;
  });
}

/**
 * Why a merge of one layer into another may not run: the target is locked or hidden, or a tile, terrain or
 * prop layer between the two in the map's order draws in a cell the source draws, whether or not its eye is
 * shut (the game draws it either way). The lowest such layer is named. The other layers merged in the same
 * replace are left out: they end in the same target, so their paint crosses nothing.
 *
 * @param map The map.
 * @param sourceId The layer merged away.
 * @param targetId The layer merged into.
 * @param env Why a layer refuses edits, a prop's art and a terrain layer's resolved tiles.
 * @param merged Every layer merged away in the same replace; none but the source by default.
 * @returns The refusal, or null when the merge may run.
 * @throws Error when the map lacks either layer or the source is not a terrain layer.
 */
export function mergeRefusal(map: MapDocument, sourceId: string, targetId: string, env: Pick<MergeEnv, "blocked" | "sizeOf" | "tileSize" | "frameOf" | "resolved">, merged: ReadonlySet<string> = new Set()): MergeRefusal | null {
  const from = map.layers.findIndex((layer) => layer.id === sourceId);
  const to = map.layers.findIndex((layer) => layer.id === targetId);
  const source = map.layers[from];
  if (source?.kind !== "terrain" || to < 0) {
    throw new Error(`map ${map.id} cannot merge layer ${sourceId} into ${targetId}`);
  }
  const why = env.blocked(targetId);
  if (why !== null) {
    return { why, layerId: targetId };
  }
  const cells = drawnCells(map, source, env);
  const between = map.layers.slice(Math.min(from, to) + 1, Math.max(from, to)).find((layer) => !merged.has(layer.id) && drawsIn(map, layer, cells, env));
  return between === undefined ? null : { why: "between", layerId: between.id };
}

/**
 * A refusal as the dialog and a refused replace say it: the layer's name and what it does.
 *
 * @param map The map.
 * @param refusal The refusal.
 * @returns The reason, naming the layer (its id when the map no longer holds it).
 */
export function mergeRefusalReason(map: MapDocument, refusal: MergeRefusal): string {
  const name = map.layers.find((layer) => layer.id === refusal.layerId)?.name ?? refusal.layerId;
  return refusal.why === "between" ? `${name} draws between them` : `${name} is ${refusal.why}`;
}

/**
 * A merge as an area and the ticks give it: the ticked layers that would change, each taken whole, the layer
 * they go into and why the merge may not run.
 */
export interface MergeParts {
  readonly sources: readonly string[];
  readonly target: string;
  readonly refused: MergeRefusal | null;
}

/**
 * The merge an area and the ticks give: the ticked terrain layers in scope that paint in the area and do not
 * already paint the entry, the layer they would go into and the first refusal among them, in the map's order.
 *
 * @param map The map.
 * @param scope The area and the layers a replace may write.
 * @param tilesets The world's tilesets.
 * @param terrain The terrain layers ticked.
 * @param to The entry they become: its terrain's id and its colour.
 * @param env What a merge needs from the editor.
 * @returns The merge, or null when no ticked layer would change, the area cuts through the paint of one, or
 *   no other layer paints the entry.
 */
export function mergeParts(map: MapDocument, scope: ReplaceScope, tilesets: readonly TilesetRef[], terrain: readonly string[], to: { readonly terrain: string; readonly colour: number }, env: MergeEnv): MergeParts | null {
  const inArea = new Map(scanReplace(map, { rect: scope.rect, layerIds: scope.layerIds.filter((layerId) => terrain.includes(layerId)) }, tilesets).flatMap((source) => (source.kind === "terrain" ? [[source.layerId, source] as const] : [])));
  const sources: string[] = [];
  for (const layer of map.layers) {
    const held = inArea.get(layer.id);
    if (layer.kind !== "terrain" || held === undefined || (held.terrain === to.terrain && held.colour === to.colour)) {
      continue;
    }
    if (layer.corners.reduce((sum, colour) => sum + (colour === 0 ? 0 : 1), 0) > held.count) {
      return null;
    }
    sources.push(layer.id);
  }
  const target = sources.length === 0 ? null : mergeTarget(map, new Set(terrain), to, env);
  if (target === null) {
    return null;
  }
  let refused: MergeRefusal | null = null;
  for (const sourceId of sources) {
    refused ??= mergeRefusal(map, sourceId, target, env, new Set(sources));
  }
  return { sources, target, refused };
}

/**
 * A terrain layer of a map by id.
 *
 * @param world The world.
 * @param mapId The map.
 * @param layerId The layer.
 * @returns The map and the layer.
 * @throws Error when the world lacks the map or the map has no terrain layer of that id.
 */
function terrainLayerOf(world: WorldDocument, mapId: string, layerId: string): { readonly map: MapDocument; readonly layer: TerrainLayer } {
  const map = world.maps.find((m) => m.id === mapId);
  const layer = map?.layers.find((l) => l.id === layerId);
  if (map === undefined || layer?.kind !== "terrain") {
    throw new Error(`layer ${layerId} is not a terrain layer of map ${mapId}`);
  }
  return { map, layer };
}

/**
 * Plans a merge as one command that inverts exactly: for each source in turn, one `fillCells` on the target
 * from 0 to the entry's colour over the corners the source sets and the target does not (none when the target
 * holds them all; a corner both set is left out, since `fillCells` refuses an index that does not hold its
 * `before` value), then the source's removal, each built against the document the one before left. The
 * target's id, name, group and place are not written. After each removal the Players band is checked to be
 * the band before, less the layer when it lay under it.
 *
 * @param world The world.
 * @param mapId The map.
 * @param sourceIds The terrain layers merged away, every corner of each.
 * @param targetId The layer merged into.
 * @param to The entry the target paints.
 * @returns The plan, or null with no source.
 * @throws Error when the map or a layer is missing or is no terrain layer, the target is a source, the
 *   entry's terrain is not a corner set or lacks the colour, the target paints another terrain or colour, or a
 *   removal leaves another band.
 */
export function planReplaceMerge(world: WorldDocument, mapId: string, sourceIds: readonly string[], targetId: string, to: ReplaceTerrainEntry): ReplaceMergePlan | null {
  if (sourceIds.length === 0) {
    return null;
  }
  if (to.terrain.type !== "corner" || !to.terrain.colors.some((colour) => colour.index === to.colour)) {
    throw new Error(`terrain ${to.terrain.id} is not a corner set with colour ${String(to.colour)}`);
  }
  const target = terrainLayerOf(world, mapId, targetId).layer;
  const held = paintedColour(target);
  if (sourceIds.includes(targetId) || target.terrain !== to.terrain.id || (held !== 0 && held !== to.colour)) {
    throw new Error(`layer ${targetId} cannot take a merge of ${to.terrain.id} colour ${String(to.colour)}`);
  }
  const commands: Command[] = [];
  let current = world;
  let corners = 0;
  for (const sourceId of sourceIds) {
    const { map, layer: source } = terrainLayerOf(current, mapId, sourceId);
    const marked = Uint8Array.from(source.corners, (colour) => (colour === 0 ? 0 : 1));
    corners += marked.reduce((sum, mark) => sum + mark, 0);
    const fills = cornerFills(map, terrainLayerOf(current, mapId, targetId).layer, marked, to.colour);
    const band = playersAbove(map);
    const index = map.layers.findIndex((layer) => layer.id === sourceId);
    for (const command of [...fills, removeLayerCommand(map, sourceId)]) {
      current = applyCommand(current, command);
      commands.push(command);
    }
    const after = terrainLayerOf(current, mapId, targetId).map;
    if (playersAbove(after) !== band - (index < band ? 1 : 0)) {
      throw new Error(`removing layer ${sourceId} would move the Players band`);
    }
  }
  const merged = terrainLayerOf(current, mapId, targetId);
  return { command: { kind: "compound", commands }, corners, layers: [targetId], removed: [...sourceIds], unresolved: unresolvedTerrainCells(to.terrain, merged.layer.corners, merged.map.width, merged.map.height).cells };
}
